var m = require('mithril');
var prop = require('mithril/stream');
var header = require('../layout/components/header');
var loaders = require('../layout/components/loaders');
var heading = require('../components/heading');
var button = require('../components/button');
var ToggleSwitch = require('../components/toggleSwitch');
var fileInput = require('../components/inputs/fileInput');
var treeEquipment = require('../locations/treeEquipment');
var treeDevice = require('../network/components/deviceTree');
var helpModal = require('../help/helpModal');
var templateDeviceSelector = require('./components/templateDeviceSelector');
var templateInputModeTabs = require('./components/templateInputModeTabs');
var bulkBindingFieldHelp = require('./components/bulkBindingFieldHelp');
var equipmentModel = require('../equipment/models/equipment');
var choices = require('../libs/choices');

var bindingPage = {};

function extractErrorMessage(error) {
  if (!error) return '';
  if (typeof error === 'string') return error;
  if (typeof error.error === 'string' && error.error) return error.error;
  if (typeof error.message === 'string' && error.message) return error.message;
  if (Array.isArray(error.errors) && error.errors.length) {
    var first = error.errors[0];
    if (typeof first === 'string') return first;
    if (first && typeof first.message === 'string') return first.message;
  }
  if (error.response && typeof error.response === 'object') {
    return extractErrorMessage(error.response);
  }
  return '';
}

function normalizeTemplateEquipmentRid(rid) {
  var trimmedRid = typeof rid === 'string' ? rid.trim() : '';
  if (!trimmedRid) return '';
  return trimmedRid.charAt(0) === '#' ? trimmedRid : ('#' + trimmedRid);
}

function normalizeEquipmentRidParam(rid) {
  return String(rid || '').trim().replace(/^#/, '');
}

function normalizeRid(value) {
  var text = value === undefined || value === null ? '' : String(value).trim();
  if (!text) return '';
  return text.charAt(0) === '#' ? text : ('#' + text);
}

function isRidFormat(value) {
  return /^#-?\d+:\d+$/.test(String(value || '').trim());
}

function normalizeBacnetServerRecord(record) {
  if (!record || typeof record !== 'object') return null;
  var rid = normalizeRid(record['@rid'] || record.rid);
  if (!rid) return null;
  return {
    rid: rid,
    name: record.name ? String(record.name).trim() : '',
    host: record.host ? String(record.host).trim() : ''
  };
}

function formatBacnetServerLabel(record) {
  var normalized = normalizeBacnetServerRecord(record);
  if (!normalized) return '';

  var name = normalized.name || '(unnamed)';
  var host = normalized.host || '';
  var ridWithoutHash = normalized.rid.replace(/^#/, '');

  return m('span', [
    m('a.link.blue.pointer.mr2', {
      href: '/#!/bacnetserver/' + encodeURIComponent(ridWithoutHash),
      target: '_blank',
      rel: 'noopener noreferrer'
    }, name),
    host ? m('span.gray', ' - (' + host + ')') : ''
  ]);
}

function resolveBacnetServerByRid(rawRid) {
  var rid = normalizeRid(rawRid);
  if (!rid) return Promise.resolve({ server: null, error: '' });
  if (!isRidFormat(rid)) {
    return Promise.resolve({ server: null, error: 'Invalid RID format. Use #12:0.' });
  }

  return loaders.requestWithLoader({
    loader: 'background',
    method: 'GET',
    url: '/api/v1/bacnetserver/' + encodeURIComponent(rid.replace(/^#/, ''))
  })
    .then(function (response) {
      var server = normalizeBacnetServerRecord(response);
      if (!server) {
        return {
          server: null,
          error: 'RID not found or it is not a BACnet Host.'
        };
      }
      return { server: server, error: '' };
    })
    .catch(function () {
      return {
        server: null,
        error: 'RID not found or it is not a BACnet Host.'
      };
    });
}

function normalizeClassTemplates(templates) {
  if (!Array.isArray(templates)) return [];
  return templates
    .map(function (template) {
      var normalizedTemplate =
        template && typeof template === 'object'
          ? template
          : { name: typeof template === 'string' ? template : '' };
      return {
        name: typeof normalizedTemplate.name === 'string' ? normalizedTemplate.name.trim() : '',
        equipmentRid: normalizeTemplateEquipmentRid(normalizedTemplate.equipmentRid)
      };
    })
    .filter(function (template) {
      return Boolean(template && template.equipmentRid);
    });
}

function collectClassNamesFromClassTree(classTree) {
  var classNameSet = new Set();
  var walkNode = function (node) {
    if (!node || typeof node !== 'object') return;
    var className = node.name ? String(node.name).trim() : '';
    if (className) {
      classNameSet.add(className);
    }
    var children = Array.isArray(node.children) ? node.children : [];
    children.forEach(walkNode);
  };
  var categories = classTree && Array.isArray(classTree.categories) ? classTree.categories : [];
  categories.forEach(walkNode);
  return Array.from(classNameSet).sort(function (a, b) {
    return a.localeCompare(b);
  });
}

function formatClassTemplateLabel(template) {
  var normalized = template && typeof template === 'object' ? template : {};
  var name = typeof normalized.name === 'string' ? normalized.name.trim() : '';
  var equipmentRid = normalizeTemplateEquipmentRid(normalized.equipmentRid);
  var classNames = Array.isArray(normalized.classNames)
    ? normalized.classNames
      .map(function (className) { return String(className || '').trim(); })
      .filter(Boolean)
    : [];
  var classSuffix = classNames.length ? (' - ' + classNames.join(', ')) : '';
  var displayName = name || 'Default';
  return equipmentRid
    ? (displayName + classSuffix + ' (Template ID ' + equipmentRid + ')')
    : (displayName + classSuffix);
}

function base64ToUint8Array(base64String) {
  var decoded = window.atob(base64String || '');
  var bytes = new Uint8Array(decoded.length);
  for (var i = 0; i < decoded.length; i++) {
    bytes[i] = decoded.charCodeAt(i);
  }
  return bytes;
}

function decodeByteArrayToText(byteArray) {
  if (typeof TextDecoder !== 'undefined') {
    return new TextDecoder('utf-8').decode(byteArray);
  }
  var text = '';
  for (var i = 0; i < byteArray.length; i++) {
    text += String.fromCharCode(byteArray[i]);
  }
  return decodeURIComponent(escape(text));
}

function safeRedraw() {
  try {
    if (typeof m.redraw === 'function') {
      m.redraw();
    }
  } catch (_error) {}
}

bindingPage.oninit = function () {
  var ctrl = this;
  header.vm.activeTab('Binding');
  document.title = 'nimrod | Binding Tools';

  ctrl.commands = prop([]);
  ctrl.selectedCommandKey = prop('');
  ctrl.loadingCommands = prop(true);
  ctrl.errorMessage = prop('');
  ctrl.validationError = prop('');
  ctrl.result = prop(null);
  ctrl.formValues = {};
  ctrl.equipmentPickers = {};
  ctrl.devicePickers = {};
  ctrl.classTemplatePicker = {
    sourceEquipmentId: '',
    classTemplates: [],
    loadingClassTemplates: false,
    classTemplatesError: '',
    selectedClassTemplateRid: '',
    selectedClassTemplateLabel: '',
    loadingSelectedTemplate: false,
    selectedTemplateError: '',
    requestId: 0
  };
  ctrl.serverPicker = {
    open: false,
    selected: prop(null),
    resolving: false,
    error: '',
    selectedLabel: '',
    selectedRecord: null,
    requestId: 0
  };
  ctrl.bulkClassFilter = {
    classNames: [],
    loading: false,
    error: '',
    loaded: false,
    requestId: 0
  };

  ctrl.isApplyTemplateCommand = function (command) {
    return Boolean(command && command.key === 'apply-template');
  };

  ctrl.resetClassTemplatePicker = function () {
    ctrl.classTemplatePicker.sourceEquipmentId = '';
    ctrl.classTemplatePicker.classTemplates = [];
    ctrl.classTemplatePicker.loadingClassTemplates = false;
    ctrl.classTemplatePicker.classTemplatesError = '';
    ctrl.classTemplatePicker.selectedClassTemplateRid = '';
    ctrl.classTemplatePicker.selectedClassTemplateLabel = '';
    ctrl.classTemplatePicker.loadingSelectedTemplate = false;
    ctrl.classTemplatePicker.selectedTemplateError = '';
  };

  ctrl.resetServerPicker = function () {
    ctrl.serverPicker.open = false;
    ctrl.serverPicker.selected(null);
    ctrl.serverPicker.resolving = false;
    ctrl.serverPicker.error = '';
    ctrl.serverPicker.selectedLabel = '';
    ctrl.serverPicker.selectedRecord = null;
    ctrl.serverPicker.requestId = 0;
  };

  ctrl.applySelectedServerRecord = function (record, options) {
    var opts = options && typeof options === 'object' ? options : {};
    var server = normalizeBacnetServerRecord(record);
    if (!server) {
      ctrl.updateValue('selectedServerRid', '');
      ctrl.serverPicker.selectedLabel = '';
      ctrl.serverPicker.selectedRecord = null;
      ctrl.serverPicker.error = '';
      if (opts.clearPickerNode === true) {
        ctrl.serverPicker.selected(null);
      }
      return;
    }
  
    ctrl.updateValue('selectedServerRid', server.rid);
    ctrl.serverPicker.error = '';
    ctrl.serverPicker.selectedRecord = server;
    ctrl.serverPicker.selectedLabel = '';
  };

  ctrl.resolveSelectedServerRid = function (value) {
    var normalized = normalizeRid(value);
    ctrl.updateValue('selectedServerRid', normalized);
    ctrl.serverPicker.error = '';
    ctrl.serverPicker.selectedLabel = '';
    ctrl.serverPicker.selectedRecord = null;
  
    if (!normalized) {
      ctrl.serverPicker.selected(null);
      return Promise.resolve();
    }
  
    var requestId = ctrl.serverPicker.requestId + 1;
    ctrl.serverPicker.requestId = requestId;
    ctrl.serverPicker.resolving = true;
    m.redraw();
  
    return resolveBacnetServerByRid(normalized)
      .then(function (result) {
        if (requestId !== ctrl.serverPicker.requestId) return;
        if (result && result.server) {
          ctrl.applySelectedServerRecord(result.server, { clearPickerNode: true });
          return;
        }
        ctrl.serverPicker.selectedLabel = '';
        ctrl.serverPicker.selectedRecord = null;
        ctrl.serverPicker.error = (result && result.error) ? result.error : 'Unable to resolve BACnet Host.';
      })
      .finally(function () {
        if (requestId !== ctrl.serverPicker.requestId) return;
        ctrl.serverPicker.resolving = false;
        m.redraw();
      });
  };

  ctrl.resolveClassTemplatesForEquipment = function (equipmentRecord) {
    var equipmentClass = equipmentRecord && equipmentRecord['@class'] ? equipmentRecord['@class'] : '';
    if (!equipmentClass) return [];
    var classRecord = equipmentModel.findClassData(equipmentClass);
    var templates = normalizeClassTemplates(classRecord && classRecord.data ? classRecord.data.templates : []);
    if (!templates.length) {
      var inheritedClassData = equipmentModel.getInheretedClassData(equipmentClass);
      templates = normalizeClassTemplates(inheritedClassData && inheritedClassData.foundEquipmentClass && inheritedClassData.foundEquipmentClass.data
        ? inheritedClassData.foundEquipmentClass.data.templates
        : []);
    }
    return templates;
  };

  ctrl.resolveClassTemplatesForBulkApply = function (classTree) {
    var templateMap = new Map();

    var appendTemplate = function (template, className) {
      var equipmentRid = normalizeTemplateEquipmentRid(template && template.equipmentRid);
      if (!equipmentRid) return;
      var templateName = template && typeof template.name === 'string' ? template.name.trim() : '';
      var normalizedClassName = className ? String(className).trim() : '';

      if (!templateMap.has(equipmentRid)) {
        templateMap.set(equipmentRid, {
          name: templateName,
          equipmentRid: equipmentRid,
          classNames: normalizedClassName ? [normalizedClassName] : []
        });
        return;
      }

      var existing = templateMap.get(equipmentRid);
      if (!existing.name && templateName) {
        existing.name = templateName;
      }
      if (normalizedClassName && existing.classNames.indexOf(normalizedClassName) === -1) {
        existing.classNames.push(normalizedClassName);
      }
    };

    var walkClassNode = function (node) {
      if (!node || typeof node !== 'object') return;
      var className = node.name ? String(node.name).trim() : '';
      var templates = normalizeClassTemplates(node.data && node.data.templates ? node.data.templates : []);
      templates.forEach(function (template) {
        appendTemplate(template, className);
      });
      var children = Array.isArray(node.children) ? node.children : [];
      children.forEach(walkClassNode);
    };

    var categories = classTree && Array.isArray(classTree.categories) ? classTree.categories : [];
    categories.forEach(walkClassNode);

    return Array.from(templateMap.values())
      .map(function (template) {
        return Object.assign({}, template, {
          classNames: (template.classNames || []).slice().sort(function (a, b) {
            return a.localeCompare(b);
          })
        });
      })
      .sort(function (a, b) {
        return formatClassTemplateLabel(a).localeCompare(formatClassTemplateLabel(b));
      });
  };

  ctrl.resolveBulkClassNamesForAutocomplete = function (classTree) {
    return collectClassNamesFromClassTree(classTree);
  };

  ctrl.fetchBulkClassNames = function (options) {
    var opts = options && typeof options === 'object' ? options : {};
    if (!opts.force && ctrl.bulkClassFilter.loaded) {
      return Promise.resolve(ctrl.bulkClassFilter.classNames.slice());
    }

    ctrl.bulkClassFilter.loading = true;
    ctrl.bulkClassFilter.error = '';
    var requestId = ctrl.bulkClassFilter.requestId + 1;
    ctrl.bulkClassFilter.requestId = requestId;

    return equipmentModel.getClassTree()
      .then(function (classTree) {
        if (requestId !== ctrl.bulkClassFilter.requestId) {
          return [];
        }
        var classNames = ctrl.resolveBulkClassNamesForAutocomplete(classTree);
        ctrl.bulkClassFilter.classNames = classNames;
        ctrl.bulkClassFilter.loaded = true;
        if (!classNames.length) {
          ctrl.bulkClassFilter.error = 'No equipment classes were found.';
        }
        return classNames.slice();
      })
      .catch(function (error) {
        if (requestId !== ctrl.bulkClassFilter.requestId) {
          return [];
        }
        ctrl.bulkClassFilter.classNames = [];
        ctrl.bulkClassFilter.error = extractErrorMessage(error) || 'Unable to load equipment classes.';
        return [];
      })
      .finally(function () {
        if (requestId !== ctrl.bulkClassFilter.requestId) {
          return;
        }
        ctrl.bulkClassFilter.loading = false;
        safeRedraw();
      });
  };

  ctrl.fetchClassTemplatesForTargetEquipment = function (targetEquipmentId, options) {
    var opts = options && typeof options === 'object' ? options : {};
    var normalizedTargetId = normalizeTemplateEquipmentRid(targetEquipmentId);
    if (!normalizedTargetId) {
      ctrl.resetClassTemplatePicker();
      return Promise.resolve([]);
    }

    if (!opts.force && ctrl.classTemplatePicker.sourceEquipmentId === normalizedTargetId) {
      return Promise.resolve(ctrl.classTemplatePicker.classTemplates.slice());
    }

    ctrl.classTemplatePicker.sourceEquipmentId = normalizedTargetId;
    ctrl.classTemplatePicker.loadingClassTemplates = true;
    ctrl.classTemplatePicker.classTemplatesError = '';
    ctrl.classTemplatePicker.selectedTemplateError = '';
    ctrl.classTemplatePicker.classTemplates = [];
    ctrl.classTemplatePicker.selectedClassTemplateRid = '';
    ctrl.classTemplatePicker.selectedClassTemplateLabel = '';

    var requestId = ctrl.classTemplatePicker.requestId + 1;
    ctrl.classTemplatePicker.requestId = requestId;

    return equipmentModel.getClassTree()
      .then(function () {
        return loaders.requestWithLoader({
          loader: 'background',
          method: 'GET',
          url: '/api/v1/equipment/' + normalizeEquipmentRidParam(normalizedTargetId)
        });
      })
      .then(function (equipmentRecord) {
        if (requestId !== ctrl.classTemplatePicker.requestId) {
          return [];
        }
        var templates = ctrl.resolveClassTemplatesForEquipment(equipmentRecord);
        ctrl.classTemplatePicker.classTemplates = templates;
        if (!templates.length) {
          ctrl.classTemplatePicker.classTemplatesError = 'No saved templates are available for this equipment class.';
        }
        return templates.slice();
      })
      .catch(function (error) {
        if (requestId !== ctrl.classTemplatePicker.requestId) {
          return [];
        }
        ctrl.classTemplatePicker.classTemplates = [];
        ctrl.classTemplatePicker.classTemplatesError = extractErrorMessage(error) || 'Unable to load templates for the selected equipment.';
        return [];
      })
      .finally(function () {
        if (requestId !== ctrl.classTemplatePicker.requestId) {
          return;
        }
        ctrl.classTemplatePicker.loadingClassTemplates = false;
        m.redraw();
      });
  };

  ctrl.fetchClassTemplatesForBulkApply = function (options) {
    var opts = options && typeof options === 'object' ? options : {};
    var sourceMarker = '__bulk__';

    if (!opts.force && ctrl.classTemplatePicker.sourceEquipmentId === sourceMarker) {
      return Promise.resolve(ctrl.classTemplatePicker.classTemplates.slice());
    }

    ctrl.classTemplatePicker.sourceEquipmentId = sourceMarker;
    ctrl.classTemplatePicker.loadingClassTemplates = true;
    ctrl.classTemplatePicker.classTemplatesError = '';
    ctrl.classTemplatePicker.selectedTemplateError = '';
    ctrl.classTemplatePicker.classTemplates = [];
    ctrl.classTemplatePicker.selectedClassTemplateRid = '';
    ctrl.classTemplatePicker.selectedClassTemplateLabel = '';

    var requestId = ctrl.classTemplatePicker.requestId + 1;
    ctrl.classTemplatePicker.requestId = requestId;

    return equipmentModel.getClassTree()
      .then(function (classTree) {
        if (requestId !== ctrl.classTemplatePicker.requestId) {
          return [];
        }
        var classNames = ctrl.resolveBulkClassNamesForAutocomplete(classTree);
        ctrl.bulkClassFilter.classNames = classNames;
        ctrl.bulkClassFilter.loaded = true;
        if (!classNames.length && !ctrl.bulkClassFilter.error) {
          ctrl.bulkClassFilter.error = 'No equipment classes were found.';
        }
        var templates = ctrl.resolveClassTemplatesForBulkApply(classTree);
        ctrl.classTemplatePicker.classTemplates = templates;
        if (!templates.length) {
          ctrl.classTemplatePicker.classTemplatesError = 'No saved templates are available across equipment classes.';
        }
        return templates.slice();
      })
      .catch(function (error) {
        if (requestId !== ctrl.classTemplatePicker.requestId) {
          return [];
        }
        ctrl.classTemplatePicker.classTemplates = [];
        ctrl.classTemplatePicker.classTemplatesError = extractErrorMessage(error) || 'Unable to load templates.';
        return [];
      })
      .finally(function () {
        if (requestId !== ctrl.classTemplatePicker.requestId) {
          return;
        }
        ctrl.classTemplatePicker.loadingClassTemplates = false;
        m.redraw();
      });
  };

  ctrl.loadClassTemplateIntoTemplateContent = function (templateRid, templateOption) {
    var selectedTemplateRid = normalizeTemplateEquipmentRid(templateRid);
    ctrl.classTemplatePicker.selectedClassTemplateRid = selectedTemplateRid;
    ctrl.classTemplatePicker.selectedClassTemplateLabel = templateOption ? formatClassTemplateLabel(templateOption) : '';
    ctrl.classTemplatePicker.selectedTemplateError = '';

    if (!selectedTemplateRid) {
      ctrl.updateValue('templateContent', '');
      ctrl.formValues.templateContent_filename = '';
      return Promise.resolve();
    }

    ctrl.classTemplatePicker.loadingSelectedTemplate = true;
    ctrl.updateValue('templateContent', '');
    ctrl.formValues.templateContent_filename = '';
    m.redraw();

    return loaders.requestWithLoader({
      loader: 'background',
      method: 'POST',
      url: '/api/v1/binding/run',
      body: {
        command: 'export-template',
        options: {
          sourceEquipmentId: selectedTemplateRid
        }
      }
    })
      .then(function (response) {
        if (!response || !response.download || !response.download.base64) {
          throw new Error('Template export did not return JSON content.');
        }
        var byteArray = base64ToUint8Array(response.download.base64);
        var rawText = decodeByteArrayToText(byteArray);
        var templateContent = rawText;
        try {
          templateContent = JSON.stringify(JSON.parse(rawText), null, 2);
        } catch (error) {}
        ctrl.updateValue('templateContent', templateContent);
        ctrl.formValues.templateContent_filename = ctrl.classTemplatePicker.selectedClassTemplateLabel || response.download.fileName || 'Template JSON';
      })
      .catch(function (error) {
        ctrl.classTemplatePicker.selectedTemplateError = extractErrorMessage(error) || 'Unable to load template JSON.';
      })
      .finally(function () {
        ctrl.classTemplatePicker.loadingSelectedTemplate = false;
        m.redraw();
      });
  };

  ctrl.activateClassTemplateMode = function (targetEquipmentId) {
    var normalizedTargetId = normalizeTemplateEquipmentRid(targetEquipmentId);
    ctrl.updateValue('templateContent_mode', 'class-template');
    ctrl.classTemplatePicker.selectedTemplateError = '';

    if (!normalizedTargetId) {
      ctrl.classTemplatePicker.classTemplatesError = 'Select Target Equipment ID first to load class templates.';
      ctrl.classTemplatePicker.classTemplates = [];
      ctrl.classTemplatePicker.selectedClassTemplateRid = '';
      ctrl.classTemplatePicker.selectedClassTemplateLabel = '';
      ctrl.updateValue('templateContent', '');
      ctrl.formValues.templateContent_filename = '';
      m.redraw();
      return Promise.resolve();
    }

    return ctrl.fetchClassTemplatesForTargetEquipment(normalizedTargetId)
      .then(function (templates) {
        if (!templates.length) {
          ctrl.classTemplatePicker.selectedClassTemplateRid = '';
          ctrl.classTemplatePicker.selectedClassTemplateLabel = '';
          ctrl.updateValue('templateContent', '');
          ctrl.formValues.templateContent_filename = '';
          return;
        }
        var selectedTemplateRid = ctrl.classTemplatePicker.selectedClassTemplateRid;
        var selectedTemplate = templates.find(function (template) {
          return template && template.equipmentRid === selectedTemplateRid;
        }) || templates[0];
        return ctrl.loadClassTemplateIntoTemplateContent(
          selectedTemplate && selectedTemplate.equipmentRid,
          selectedTemplate
        );
      });
  };

  ctrl.activateBulkClassTemplateMode = function () {
    ctrl.updateValue('templateContent_mode', 'class-template');
    ctrl.classTemplatePicker.selectedTemplateError = '';

    return ctrl.fetchClassTemplatesForBulkApply()
      .then(function (templates) {
        if (!templates.length) {
          ctrl.classTemplatePicker.selectedClassTemplateRid = '';
          ctrl.classTemplatePicker.selectedClassTemplateLabel = '';
          ctrl.updateValue('templateContent', '');
          ctrl.formValues.templateContent_filename = '';
          return;
        }
        var selectedTemplate = templates.find(function (template) {
          return template && template.equipmentRid === ctrl.classTemplatePicker.selectedClassTemplateRid;
        }) || templates[0];
        return ctrl.loadClassTemplateIntoTemplateContent(
          selectedTemplate && selectedTemplate.equipmentRid,
          selectedTemplate
        );
      });
  };

  ctrl.refreshClassTemplatesFromTargetEquipment = function (targetEquipmentId) {
    var selectedCommand = ctrl.commands().find(function (cmd) {
      return cmd && cmd.key === ctrl.selectedCommandKey();
    });
    if (!ctrl.isApplyTemplateCommand(selectedCommand)) {
      return Promise.resolve();
    }
    return ctrl.fetchClassTemplatesForTargetEquipment(targetEquipmentId, { force: true })
      .then(function (templates) {
        var currentMode = ctrl.getValue('templateContent_mode', 'paste');
        if (currentMode !== 'class-template') {
          return;
        }
        if (!templates.length) {
          ctrl.updateValue('templateContent', '');
          ctrl.formValues.templateContent_filename = '';
          return;
        }
        var selectedTemplate = templates.find(function (template) {
          return template && template.equipmentRid === ctrl.classTemplatePicker.selectedClassTemplateRid;
        }) || templates[0];
        return ctrl.loadClassTemplateIntoTemplateContent(
          selectedTemplate && selectedTemplate.equipmentRid,
          selectedTemplate
        );
      });
  };

  ctrl.getEquipmentPicker = function (key) {
    if (!ctrl.equipmentPickers[key]) {
      ctrl.equipmentPickers[key] = {
        show: false,
        selected: prop(null)
      };
    }
    return ctrl.equipmentPickers[key];
  };

  ctrl.getDevicePicker = function (key) {
    if (!ctrl.devicePickers[key]) {
      ctrl.devicePickers[key] = {
        selected: prop(null),
        requiredSelections: [],
        expandedIndex: -1,
        overrideSelections: [{
          templateKey: key + '-override-0',
          rid: '',
          name: '',
          path: ''
        }],
        overrideExpandedIndex: -1,
        overrideSelected: prop(null)
      };
    }
    return ctrl.devicePickers[key];
  };

  ctrl.isEquipmentOption = function (option) {
    if (!option) return false;
    if (option.key === 'class') return false;
    if (option.key === 'search') return false;
    var key = (option.key || '').toLowerCase();
    var label = (option.label || '').toLowerCase();
    var hasEquipment = key.indexOf('equipment') !== -1 || label.indexOf('equipment') !== -1;
    if (!hasEquipment) {
      return false;
    }
    var mentionsDevice = key.indexOf('device') !== -1 || label.indexOf('device') !== -1;
    return !mentionsDevice;
  };

  ctrl.isDeviceOption = function (option, command) {
    if (!option) return false;
    if (ctrl.isBulkTemplateCommand(command)) return false;
    var key = (option.key || '').toLowerCase();
    var label = (option.label || '').toLowerCase();
    if (key === 'deviceprefixes') return false;
    var mentionsDevice = key.indexOf('device') !== -1 || label.indexOf('device') !== -1;
    if (!mentionsDevice) return false;
    var mentionsNameOnly = key.indexOf('name') !== -1 || label.indexOf('name') !== -1;
    if (mentionsNameOnly) return false;
    var expectsIdentifier =
      key.indexOf('rid') !== -1 ||
      key.indexOf('id') !== -1 ||
      label.indexOf('rid') !== -1 ||
      label.indexOf('id') !== -1 ||
      key.indexOf('ref') !== -1 ||
      label.indexOf('ref') !== -1;
    if (!expectsIdentifier) return false;
    var mentionsEquipment = key.indexOf('equipment') !== -1 || label.indexOf('equipment') !== -1;
    return !mentionsEquipment;
  };

  ctrl.isBulkTemplateCommand = function (command) {
    return Boolean(command && command.key === 'apply-template-bulk');
  };

  ctrl.describeOptionType = function (option) {
    var type = option && option.type ? option.type : 'string';
    if (type === 'boolean') {
      return 'Boolean toggle (enabled when checked).';
    }
    if (type === 'list') {
      return 'Multi-line text (enter one value per line, blank lines are ignored).';
    }
    if (type === 'text') {
      return 'Multi-line text input (paste JSON or longer content).';
    }
    return 'Single-line text input.';
  };

  ctrl.escapeBackticks = function (value) {
    return String(value || '').replace(/`/g, '\\`');
  };

  ctrl.buildCommandsHelpMarkdown = function () {
    var commands = ctrl.commands();
    if (!Array.isArray(commands) || !commands.length) {
      return 'Command details are still loading. Open this help again once the commands are available.';
    }

    var lines = [
      'Binding Tools provide workflows for exporting, applying, and copying equipment bindings.',
      '',
      'Select a command to learn what it does and which fields it uses:'
    ];

    commands.forEach(function (command) {
      lines.push('');
      lines.push('## ' + command.label);
      var summary = command.description || '';
      if (summary) {
        lines.push('');
        lines.push(summary);
      }
      if (command.notes) {
        lines.push('');
        lines.push(command.notes);
      }

      if (command.options && command.options.length) {
        lines.push('');
        lines.push('### Fields');
        command.options.forEach(function (option) {
          var requirement = option.required ? 'required' : 'optional';
          var typeDescription = ctrl.describeOptionType(option).replace(/\.$/, '');
          var detail = option.description || '';
          var bullet = '- **' + option.label + '** (`' + option.key + '`, ' + requirement + ', ' + typeDescription + '): ';
          var segments = [];
          if (detail) {
            var trimmedDetail = detail.trim();
            if (trimmedDetail.length && !/[.!?]$/.test(trimmedDetail.slice(-1))) {
              trimmedDetail += '.';
            }
            segments.push(trimmedDetail);
          }
          if (option.example) {
            segments.push('Example: `' + ctrl.escapeBackticks(option.example) + '`.');
          }
          if (option.placeholder) {
            segments.push('Placeholder hint: `' + ctrl.escapeBackticks(option.placeholder) + '`.');
          }
          if (option.type === 'boolean') {
            segments.push('Default state: disabled.');
          }
          if (!segments.length) {
            segments.push('No additional details provided.');
          }
          bullet += segments.join(' ');
          lines.push(bullet);
        });
      }
    });

    lines.push('');
    lines.push('_Tip: Click a field help icon for focused guidance._');

    return lines.join('\n');
  };

  ctrl.buildOptionHelpMarkdown = function (command, option) {
    if (!command || !option) {
      return 'Help details were not found for this field.';
    }

    if (command.key === 'apply-template-bulk') {
      var sharedHelp = bulkBindingFieldHelp.getBulkBindingFieldHelp(option.key);
      if (sharedHelp && sharedHelp.markdown) {
        return sharedHelp.markdown;
      }
    }

    var detail = option.description ? option.description.trim() : '';
    var lines = [];
    if (detail) {
      lines.push(detail);
      lines.push('');
    } else {
      lines.push('No additional details provided for this field yet.');
      lines.push('');
    }
    lines.push('**Required:** ' + (option.required ? 'Yes' : 'No'));
    lines.push('**Type:** ' + ctrl.describeOptionType(option));
    if (option.example) {
      lines.push('');
      lines.push('**Example:** `' + ctrl.escapeBackticks(option.example) + '`');
    }
    var placeholder = option && option.placeholder ? option.placeholder.trim() : '';
    if (placeholder) {
      lines.push('');
      lines.push('**Placeholder hint:** `' + ctrl.escapeBackticks(placeholder) + '`');
    }
    if (option.type === 'boolean') {
      lines.push('');
      lines.push('**Default:** Disabled. Enable the toggle to include this flag in the command payload.');
    } else if (option.type === 'list') {
      lines.push('');
      lines.push('**Entry format:** Provide one value per line. Blank lines are ignored.');
    } else if (option.type === 'text') {
      lines.push('');
      lines.push('**Entry format:** Paste or type multi-line content such as JSON.');
    }
    if (ctrl.isEquipmentOption(option)) {
      lines.push('');
      lines.push('_Picker tip_: use **Browse Equipment** to choose an equipment record and fill its `@rid` automatically.');
    }
    if (ctrl.isDeviceOption(option, command)) {
      lines.push('');
      lines.push('_Picker tip_: use **Browse Devices** to add one or more device RIDs without typing.');
    }
    lines.push('');
    lines.push('_Need more context? Open the Binding Tools overview from the page header._');
    return lines.join('\n');
  };

  ctrl.openBindingHelp = function () {
    helpModal.editDialogue('Binding Tools Help', undefined, ctrl.buildCommandsHelpMarkdown());
  };

  ctrl.openOptionHelp = function (commandKey, optionKey) {
    var commands = ctrl.commands();
    if (!Array.isArray(commands) || !commands.length) {
      helpModal.editDialogue('Binding Field Help', undefined, 'Command details are not available yet. Load the commands and try again.');
      return;
    }

    var command = commands.find(function (cmd) { return cmd.key === commandKey; });
    var option = command && command.options ? command.options.find(function (opt) { return opt.key === optionKey; }) : null;
    var title = ('Field Help');
    if (command && option) {
      title = command.label + ' • ' + option.label;
    }
    helpModal.editDialogue(title, undefined, ctrl.buildOptionHelpMarkdown(command, option));
  };

  ctrl.renderOptionHelpIcon = function (commandKey, option) {
    if (!option) {
      return null;
    }
    return m('i.ion-ios-help-outline.white.f3.ml2.pointer', {
      title: 'Help for ' + option.label,
      role: 'button',
      tabindex: '0',
      'aria-label': 'Help for ' + option.label,
      onclick: function (event) {
        if (event && typeof event.preventDefault === 'function') {
          event.preventDefault();
        }
        ctrl.openOptionHelp(commandKey, option.key);
      },
      onkeypress: function (event) {
        if (event && (event.key === 'Enter' || event.key === ' ')) {
          event.preventDefault();
          ctrl.openOptionHelp(commandKey, option.key);
        }
      }
    });
  };

  ctrl.selectCommand = function (key) {
    ctrl.selectedCommandKey(key || '');
    ctrl.formValues = {};
    ctrl.result(null);
    ctrl.validationError('');
    ctrl.errorMessage('');
    ctrl.equipmentPickers = {};
    ctrl.devicePickers = {};
    ctrl.resetClassTemplatePicker();
    ctrl.resetServerPicker();
    if (key === 'apply-template-bulk') {
      ctrl.fetchBulkClassNames();
    }
  };

  ctrl.updateValue = function (key, value) {
    ctrl.formValues[key] = value;
  };

  ctrl.getValue = function (key, defaultValue) {
    if (Object.prototype.hasOwnProperty.call(ctrl.formValues, key)) {
      return ctrl.formValues[key];
    }
    return defaultValue;
  };

  ctrl.triggerDownload = function (download) {
    if (!download || !download.base64) {
      return;
    }

    try {
      var byteCharacters = window.atob(download.base64);
      var byteNumbers = new Array(byteCharacters.length);
      for (var i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      var byteArray = new Uint8Array(byteNumbers);
      var blob = new Blob([byteArray], { type: download.contentType || 'application/json' });
      var link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = download.fileName || 'binding-template.json';
      document.body.appendChild(link);
      link.click();
      setTimeout(function () {
        window.URL.revokeObjectURL(link.href);
        document.body.removeChild(link);
      }, 0);
    } catch (e) {
      ctrl.errorMessage('Failed to prepare download: ' + (e && e.message ? e.message : 'Unknown error'));
    }
  };

  ctrl.fetchCommands = function () {
    loaders.requestWithLoader({
      loader: 'main',
      method: 'GET',
      url: '/api/v1/binding/commands'
    })
      .then(function (response) {
        var list = (response && response.commands) ? response.commands : [];
        ctrl.commands(list);
        if (list.length > 0) {
          ctrl.selectCommand(list[0].key);
        }
        ctrl.loadingCommands(false);
      })
      .catch(function (error) {
        ctrl.loadingCommands(false);
        var message = (error && error.error) || (error && error.message) || 'Failed to load binding commands.';
        ctrl.errorMessage(message);
      });
  };

  ctrl.submit = function (e) {
    if (e) {
      e.preventDefault();
    }
    var commands = ctrl.commands();
    var selected = commands.find(function (cmd) { return cmd.key === ctrl.selectedCommandKey(); });
    if (!selected) {
      ctrl.validationError('Please choose a command to run.');
      return;
    }

    ctrl.validationError('');
    ctrl.errorMessage('');

    if (selected.key === 'apply-template-bulk') {
      var restrictToServerEnabled = ctrl.getValue('restrictToServer', false) === true;
      var selectedServerRid = normalizeRid(ctrl.getValue('selectedServerRid', ''));
      if (restrictToServerEnabled && !selectedServerRid) {
        ctrl.validationError('BACnet Host is required when Search for is set to Device Name.');
        return;
      }
      if (restrictToServerEnabled && !isRidFormat(selectedServerRid)) {
        ctrl.validationError('BACnet Host RID must use format #12:0 when Search for is set to Device Name.');
        return;
      }
      var classFilterValue = String(ctrl.getValue('class', '') || '').trim();
      if (
        classFilterValue &&
        ctrl.bulkClassFilter.loaded === true &&
        Array.isArray(ctrl.bulkClassFilter.classNames) &&
        ctrl.bulkClassFilter.classNames.indexOf(classFilterValue) === -1
      ) {
        ctrl.validationError('Equipment Class Filter must be a valid equipment class.');
        return;
      }
    }

    var payloadOptions = {};
    for (var i = 0; i < selected.options.length; i++) {
      var option = selected.options[i];
      if (ctrl.isBulkTemplateCommand(selected) && option.key === 'targetDeviceIds') {
        continue;
      }
      if (
        selected.key === 'apply-template-bulk' &&
        option.key === 'selectedServerRid' &&
        ctrl.getValue('restrictToServer', false) !== true
      ) {
        continue;
      }
      var rawValue = ctrl.getValue(option.key, option.type === 'boolean' ? false : '');
      if (option.required) {
        if (option.type === 'boolean') {
          // booleans are optional even when marked required (for completeness)
        } else if (option.type === 'list') {
          var listString = Array.isArray(rawValue) ? rawValue.join('\n') : String(rawValue || '');
          if (!listString.trim().length) {
            ctrl.validationError(option.label + ' is required.');
            return;
          }
        } else {
          if (rawValue === undefined || rawValue === null || String(rawValue).trim() === '') {
            ctrl.validationError(option.label + ' is required.');
            return;
          }
        }
      }

      if (option.type === 'boolean') {
        if (rawValue === true) {
          payloadOptions[option.key] = true;
        }
      } else if (option.type === 'list') {
        var listValue = Array.isArray(rawValue) ? rawValue.join('\n') : String(rawValue || '');
        if (listValue.trim().length) {
          payloadOptions[option.key] = listValue;
        }
      } else {
        var strValue = String(rawValue || '').trim();
        if (strValue.length) {
          payloadOptions[option.key] = strValue;
        }
      }
    }

    var expectsTemplate = selected.options.some(function (opt) {
      return opt.key === 'templateContent';
    });
    if (expectsTemplate) {
      var hasTemplateContent = Boolean(payloadOptions.templateContent && String(payloadOptions.templateContent).trim().length);
      if (!hasTemplateContent) {
        ctrl.validationError('Please provide template JSON content.');
        return;
      }
    }

    loaders.requestWithLoader({
      loader: 'submit',
      method: 'POST',
      url: '/api/v1/binding/run',
      body: {
        command: selected.key,
        options: payloadOptions
      }
    })
      .then(function (response) {
        ctrl.result(response);
        if (response && response.download) {
          ctrl.triggerDownload(response.download);
        }
      })
      .catch(function (error) {
        // Capture payload from API, including stdout/stderr when available
        if (error && (error.stdout || error.stderr || error.exitCode !== undefined)) {
          ctrl.result({
            success: false,
            exitCode: error.exitCode,
            stdout: error.stdout,
            stderr: error.stderr
          });
          ctrl.errorMessage((error && error.error) || 'Command failed.');
        } else {
          ctrl.errorMessage((error && error.error) || (error && error.message) || 'Unexpected error running command.');
          ctrl.result(null);
        }
      });
  };

  ctrl.fetchCommands();
};

bindingPage.view = function () {
  var ctrl = this;

  if (ctrl.loadingCommands()) {
    return m('div.mh3.mt5', [
      m('div.f3', 'Loading binding tools...')
    ]);
  }

  var commands = ctrl.commands();
  var selected = commands.find(function (cmd) { return cmd.key === ctrl.selectedCommandKey(); });

  return m('div.mh3.mb5', [
    m(heading, {
      label: 'Binding Tools',
      class: 'f-subheadline mb3 pb1 display'
    }, [
      m('i.ion-ios-help-outline.white.f2.ml2.pointer', {
        title: 'Open Binding Tools Help',
        role: 'button',
        tabindex: '0',
        'aria-label': 'Open Binding Tools Help',
        onclick: function (event) {
          if (event && typeof event.preventDefault === 'function') {
            event.preventDefault();
          }
          ctrl.openBindingHelp();
        },
        onkeypress: function (event) {
          if (event && (event.key === 'Enter' || event.key === ' ')) {
            event.preventDefault();
            ctrl.openBindingHelp();
          }
        }
      })
    ]),
    ctrl.errorMessage() ? m('div.ba.b--light-red.bg-washed-red.dark-red.pa3.br2.mb3', ctrl.errorMessage()) : null,
    ctrl.validationError() ? m('div.ba.b--gold.bg-washed-yellow.dark-gray.pa3.br2.mb3', ctrl.validationError()) : null,
    commands.length ? m('div.mv3', [
      m('label.db.mb2.f3.fw6', { for: 'binding-command-select' }, 'Command'),
      m('select#binding-command-select.ba.b--mid-gray.br2.ph2.pv2.w-100.w-50-ns', {
        value: ctrl.selectedCommandKey(),
        onchange: function (e) {
          ctrl.selectCommand(e.target.value);
        }
      }, commands.map(function (cmd) {
        return m('option', { value: cmd.key }, cmd.label);
      })),
      selected && selected.description ? m('div.mt2.mid-gray', selected.description) : null
    ]) : null,
    selected ? m('form.mt4', { onsubmit: ctrl.submit }, [
      selected.options.map(function (option) {
        if (ctrl.isBulkTemplateCommand(selected) && option.key === 'targetDeviceIds') {
          return null;
        }
        if (selected.key === 'apply-template-bulk' && (option.key === 'selectedServerRid' || option.key === 'searchPattern')) {
          return null;
        }
        var inputId = 'binding-option-' + option.key;
        var optionHelpIcon = ctrl.renderOptionHelpIcon(selected.key, option);
        var isBulkSearchModeOption = selected.key === 'apply-template-bulk' && option.key === 'restrictToServer';
        if (isBulkSearchModeOption) {
          var restrictToServerEnabled = ctrl.getValue('restrictToServer', false) === true;
          var searchMode = restrictToServerEnabled ? 'device-name' : 'server-name';
          var selectedServerOption = selected.options.find(function (commandOption) {
            return commandOption && commandOption.key === 'selectedServerRid';
          }) || null;
          var selectedServerInputId = 'binding-option-selectedServerRid';
          var selectedServerOptionHelpIcon = selectedServerOption
            ? ctrl.renderOptionHelpIcon(selected.key, selectedServerOption)
            : null;
          var selectedServerFieldLabel = selectedServerOption
            ? selectedServerOption.label + (selectedServerOption.required ? ' *' : '')
            : 'BACnet Host *';
          var currentServerRid = normalizeRid(ctrl.getValue('selectedServerRid', ''));
          var searchPatternOption = selected.options.find(function (commandOption) {
            return commandOption && commandOption.key === 'searchPattern';
          }) || null;
          var searchPatternInputId = 'binding-option-searchPattern';
          var searchPatternFieldLabel = (restrictToServerEnabled ? 'Device Name' : 'Host Name') +
            ((searchPatternOption && searchPatternOption.required) ? ' *' : '');
          var searchPatternValue = ctrl.getValue('searchPattern', '');
          var searchPatternOptionHelpIcon = searchPatternOption
            ? ctrl.renderOptionHelpIcon(selected.key, searchPatternOption)
            : null;
          var setSearchMode = function (nextMode) {
            var restrictSearchToSelectedServer = nextMode === 'device-name';
            ctrl.updateValue('restrictToServer', restrictSearchToSelectedServer);
            if (restrictSearchToSelectedServer) {
              ctrl.serverPicker.open = true;
              ctrl.serverPicker.error = '';
            } else {
              ctrl.serverPicker.open = false;
            }
          };

          return m('div.mb3.ba.b--moon-gray.br2.pa3', [
            m('div.flex.items-center.justify-between.mb2', [
              m('div.flex.items-center', [
                m('span.f4', 'Search for'),
                optionHelpIcon
              ]),
              m(templateInputModeTabs, {
                className: 'flex',
                activeKey: searchMode,
                onSelect: setSearchMode,
                tabs: [
                  { key: 'server-name', label: 'Host Name' },
                  { key: 'device-name', label: 'Device Name' }
                ]
              })
            ]),
            restrictToServerEnabled
              ? m('div', [
                  m('div.flex.items-center.mb2', [
                    m('label.db.f3.mb0', { for: selectedServerInputId }, selectedServerFieldLabel),
                    selectedServerOptionHelpIcon
                  ]),
                  m('div.flex.items-start.mb2', [
                    m('input.ba.b--mid-gray.br2.pa2.mr2', {
                      id: selectedServerInputId,
                      type: 'text',
                      value: currentServerRid,
                      style: {
                        width: '10rem',
                        minWidth: '10rem'
                      },
                      oninput: function (event) {
                        ctrl.updateValue('selectedServerRid', event && event.target ? event.target.value : '');
                        ctrl.serverPicker.error = '';
                        ctrl.serverPicker.selectedLabel = '';
                        ctrl.serverPicker.selectedRecord = null;
                      },
                      onblur: function (event) {
                        ctrl.resolveSelectedServerRid(event && event.target ? event.target.value : '');
                      }
                    }),
                    m('div.flex-auto.pt2.f6.gray', {
                      style: {
                        minHeight: '2.5rem',
                        wordBreak: 'break-word'
                      }
                    }, ctrl.serverPicker.selectedRecord
                      ? formatBacnetServerLabel(ctrl.serverPicker.selectedRecord)
                      : 'Selected Host details will appear here')
                  ]),
                  m('div.flex.items-center.mb2', [
                    m(button, {
                      label: ctrl.serverPicker.open ? 'Hide Host Browser' : 'Browse BACnet Hosts',
                      class: 'dib ph3 pv2',
                      func: function () {
                        ctrl.serverPicker.open = !ctrl.serverPicker.open;
                      }
                    })
                  ]),
                  ctrl.serverPicker.resolving
                    ? m('div.f6.gray.mb2', 'Validating BACnet Host RID...')
                    : null,
                  ctrl.serverPicker.error
                    ? m('div.f6.red.mb2', ctrl.serverPicker.error)
                    : null,
                  ctrl.serverPicker.open ? m('div.ba.b--moon-gray.br2.pa2.mb2', [
                    m(treeDevice, {
                      selectedNode: ctrl.serverPicker.selected,
                      loader: 'background',
                      itemsPerPage: 15,
                      hideLabel: true,
                      selectableClasses: ['BacnetServer'],
                      selectOnLabelClick: true
                    }),
                    m('div.mt3.tr', [
                      m(button, {
                        label: 'Use Selected BACnet Host',
                        class: 'btn-good dib ph3 pv2 ' + ((ctrl.serverPicker.selected() && ctrl.serverPicker.selected()['@rid']) ? '' : 'o-50'),
                        func: function () {
                          var selectedServer = ctrl.serverPicker.selected();
                          if (!selectedServer || !selectedServer['@rid']) return;
                          ctrl.applySelectedServerRecord(selectedServer);
                          ctrl.serverPicker.open = false;
                        }
                      })
                    ])
                  ]) : null
                ])
              : null,
            m('div.flex.items-center.mb2', [
              m('label.db.f3.mb0', { for: searchPatternInputId }, searchPatternFieldLabel),
              searchPatternOptionHelpIcon
            ]),
            m('input.w-100.ba.b--mid-gray.br2.pa2', {
              id: searchPatternInputId,
              type: 'text',
              value: searchPatternValue,
              placeholder: restrictToServerEnabled ? 'AHU-01' : 'BACnet-Host-01',
              oninput: function (event) {
                ctrl.updateValue('searchPattern', event && event.target ? event.target.value : '');
              }
            }),
            m('div.f6.gray.mt1', restrictToServerEnabled
              ? [
                  'This search pattern is used to find a device by the equipment name. Leave it blank to use the equipment name. Or enter a string and use ',
                  m('code', '{Name}'),
                  ' to inject the equipment name. e.g. ',
                  m('code', 'Example Building - Level 4 - {Name}'),
                  ' searches for a device named ',
                  m('code', 'Example Building - Level 4 - AHU-3')
                ]
              : [
                  'This search pattern is used to find a BACnet Host by the equipment name. Leave it blank to use the equipment name. Or enter a string and use ',
                  m('code', '{Name}'),
                  ' to inject the equipment name. e.g. ',
                  m('code', 'Example Building - Level 4 - {Name}'),
                  ' searches for a BACnet Host named ',
                  m('code', 'Example Building - Level 4 - AHU-3')
                ])
          ]);
        }

        if (option.type === 'boolean') {
          var checked = ctrl.getValue(option.key, false) === true;
          return m('div.mb3', [
            m('div.flex.items-center.justify-between.mb1', [
              m('div.flex.items-center', [
                m('span.f5', option.label + (option.required ? ' *' : '')),
                optionHelpIcon
              ]),
              m(ToggleSwitch, {
                id: inputId,
                checked: checked,
                ariaLabel: option.label || 'Toggle',
                onchange: function () {
                  var nextChecked = !checked;
                  ctrl.updateValue(option.key, nextChecked);
                  if (selected.key === 'apply-template-bulk' && option.key === 'restrictToServer') {
                    if (nextChecked) {
                      ctrl.serverPicker.open = true;
                      ctrl.serverPicker.error = '';
                    } else {
                      ctrl.serverPicker.open = false;
                    }
                  }
                }
              })
            ])
          ]);
        }

        var value = ctrl.getValue(option.key, '');
        var attrs = {
          id: inputId,
          placeholder: option.placeholder || '',
          oninput: function (e) {
            ctrl.updateValue(option.key, e.target.value);
          }
        };

        var isBulkSearchPatternField = selected.key === 'apply-template-bulk' && option.key === 'searchPattern';
        var isBulkClassFilterField = selected.key === 'apply-template-bulk' && option.key === 'class';
        var restrictToServerEnabledForSearch = ctrl.getValue('restrictToServer', false) === true;
        var fieldLabel = option.label + (option.required ? ' *' : '');
        if (isBulkSearchPatternField) {
          var searchLabelBase = restrictToServerEnabledForSearch ? 'Device Search Pattern' : 'BACnet Host Search Pattern';
          fieldLabel = searchLabelBase + (option.required ? ' *' : '');
          attrs.placeholder = restrictToServerEnabledForSearch ? 'AHU-01' : 'BACnet-Host-01';
        }
        if (isBulkClassFilterField) {
          attrs.placeholder = attrs.placeholder || 'AirHandler';
        }
        var isApplyTemplateTargetEquipmentField =
          ctrl.isApplyTemplateCommand(selected) && option.key === 'targetEquipmentId';

        if (isBulkClassFilterField) {
          var classNames = Array.isArray(ctrl.bulkClassFilter.classNames) ? ctrl.bulkClassFilter.classNames : [];
          var classChoicesData = classNames.map(function (className) {
            return {
              key: className,
              value: className
            };
          });
          var classValueAccessor = function (nextValue) {
            if (arguments.length) {
              ctrl.updateValue(option.key, nextValue || '');
            }
            return ctrl.getValue(option.key, '');
          };
          return m('div.mb3', [
            m('div.flex.items-center.mb2', [
              m('label.db.f3.mb0', { for: inputId }, fieldLabel),
              optionHelpIcon
            ]),
            m(choices, {
              data: classChoicesData,
              value: classValueAccessor,
              multiple: false,
              containerInnerClass: 'choices__inner_transparent',
              placeholderValue: attrs.placeholder || 'Select Equipment Class',
              onchange: function () {
                m.redraw();
              }
            }),
            ctrl.bulkClassFilter.loading ? m('div.f6.gray.mt1', 'Loading equipment classes...') : null,
            ctrl.bulkClassFilter.error ? m('div.f6.gray.mt1', ctrl.bulkClassFilter.error) : null,
            m('div.f6.gray.mt1', 'Search and select a class from the available equipment classes.')
          ]);
        }

        if (ctrl.isEquipmentOption(option)) {
          var picker = ctrl.getEquipmentPicker(option.key);
          var currentValue = ctrl.getValue(option.key, '');
          var equipmentAttrs = Object.assign({}, attrs);
          if (isApplyTemplateTargetEquipmentField) {
            equipmentAttrs.onblur = function (event) {
              var nextValue = event && event.target ? event.target.value : '';
              ctrl.updateValue(option.key, nextValue);
              ctrl.refreshClassTemplatesFromTargetEquipment(nextValue);
            };
          }
          return m('div.mb3', [
            m('div.flex.items-center.mb2', [
              m('label.db.f3.mb0', { for: inputId }, fieldLabel),
              optionHelpIcon
            ]),
            m('input.w-100.ba.b--mid-gray.br2.pa2', Object.assign({
              type: 'text',
              value: currentValue
            }, equipmentAttrs)),
            m('div.flex.items-center.mt2', [
              m(button, {
                label: picker.show ? 'Hide Equipment Picker' : 'Browse Equipment',
                class: 'dib ph3 pv2 ' + (picker.show ? 'bg-mid-gray near-white' : ''),
                func: function () {
                  picker.show = !picker.show;
                  if (!picker.show) {
                    picker.selected(null);
                  }
                }
              }),
              currentValue ? m('span.ml3.f6.mid-gray', 'Current: ' + currentValue) : null
            ]),
            picker.show ? m('div.mt3.ba.b--mid-gray.br2.pa3', [
              m(treeEquipment, { selectedNode: picker.selected }),
              m('div.mt3.tr', [
                m(button, {
                  label: 'Use Selected Equipment',
                  class: 'btn-good dib ' + (picker.selected() ? '' : 'o-50'),
                  func: function () {
                    var chosen = picker.selected();
                    if (!chosen || !chosen['@rid']) {
                      return;
                    }
                    ctrl.updateValue(option.key, chosen['@rid']);
                    if (isApplyTemplateTargetEquipmentField) {
                      ctrl.refreshClassTemplatesFromTargetEquipment(chosen['@rid']);
                    }
                    picker.show = false;
                    picker.selected(null);
                  }
                })
              ]),
              picker.selected() ?
                m('div.mt2.f6.mid-gray', 'Selected: ' + (picker.selected().name ? picker.selected().name + ' (' + picker.selected()['@rid'] + ')' : picker.selected()['@rid'])) :
                m('div.mt2.f6.mid-gray', 'Select equipment from the tree to populate the field.')
            ]) : null
          ]);
        }

        if (ctrl.isDeviceOption(option, selected)) {
          var devicePicker = ctrl.getDevicePicker(option.key);
          var currentDeviceValue = ctrl.getValue(option.key, '');
          var commandHasTemplateContent = selected.options.some(function (commandOption) {
            return commandOption && commandOption.key === 'templateContent';
          });
          var parsedRequiredDevices = commandHasTemplateContent
            ? templateDeviceSelector.parseRequiredDevicesFromTemplateText(ctrl.getValue('templateContent', ''))
            : { devices: [], error: '' };
          var requiredDevices = parsedRequiredDevices && Array.isArray(parsedRequiredDevices.devices)
            ? parsedRequiredDevices.devices
            : [];
          var templateDeviceParseError = parsedRequiredDevices && parsedRequiredDevices.error
            ? parsedRequiredDevices.error
            : '';
          if (requiredDevices.length) {
            devicePicker.requiredSelections = templateDeviceSelector.buildSelectionList(
              requiredDevices,
              currentDeviceValue,
              devicePicker.requiredSelections,
              { preserveCustomRows: true }
            );
            if (devicePicker.expandedIndex >= requiredDevices.length) {
              devicePicker.expandedIndex = -1;
              devicePicker.selected(null);
            }
            devicePicker.overrideExpandedIndex = -1;
            devicePicker.overrideSelected(null);
          } else {
            devicePicker.requiredSelections = [];
            devicePicker.expandedIndex = -1;
            var seededOverrideSelections = templateDeviceSelector.parseDeviceIdList(currentDeviceValue)
              .map(function (rid, idx) {
                return {
                  templateKey: option.key + '-override-seeded-' + idx,
                  rid: rid,
                  name: '',
                  path: ''
                };
              });
            var baseOverrideSelections = (devicePicker.overrideSelections && devicePicker.overrideSelections.length)
              ? devicePicker.overrideSelections
              : seededOverrideSelections;
            var nextOverrideSelections = templateDeviceSelector.buildSelectionList(
              [],
              currentDeviceValue,
              baseOverrideSelections,
              { preserveCustomRows: true }
            );
            if (!nextOverrideSelections.length) {
              nextOverrideSelections = [{
                templateKey: option.key + '-override-0',
                rid: '',
                name: '',
                path: ''
              }];
            }
            devicePicker.overrideSelections = nextOverrideSelections;
          }
          return m('div.mb3', [
            m('div.flex.items-center.mb2', [
              m('label.db.f3.mb0', { for: inputId }, fieldLabel),
              optionHelpIcon
            ]),
            requiredDevices.length
              ? templateDeviceSelector.renderRequiredDeviceSelector({
                  requiredDevices: requiredDevices,
                  selections: devicePicker.requiredSelections,
                  expandedIndex: devicePicker.expandedIndex,
                  selectedNode: devicePicker.selected,
                  allowAddRemoveRows: true,
                  sectionLabel: 'Required Devices',
                  rowCountLabel: '',
                  sectionHint: templateDeviceSelector.buildTargetDeviceHelpText(requiredDevices.length),
                  onToggleExpand: function (index) {
                    devicePicker.expandedIndex = devicePicker.expandedIndex === index ? -1 : index;
                    devicePicker.selected(null);
                  },
                  onSelect: function (index, selectedDevice) {
                    if (!selectedDevice || !selectedDevice['@rid']) return;
                    var requiredDevice = requiredDevices[index] || {};
                    devicePicker.requiredSelections[index] = {
                      templateKey: requiredDevice.key,
                      rid: selectedDevice['@rid'],
                      name: selectedDevice.name ? String(selectedDevice.name) : '',
                      path: selectedDevice.path ? String(selectedDevice.path) : '',
                      error: ''
                    };
                    ctrl.updateValue(option.key, templateDeviceSelector.composeTargetDeviceIds(devicePicker.requiredSelections));
                    devicePicker.expandedIndex = -1;
                    devicePicker.selected(null);
                  },
                  onChangeRid: function (index, value) {
                    var requiredDevice = requiredDevices[index] || {};
                    var nextRid = String(value || '').trim();
                    var existing = devicePicker.requiredSelections[index] || {};
                    devicePicker.requiredSelections[index] = {
                      templateKey: requiredDevice.key,
                      rid: nextRid,
                      name: existing.rid === nextRid ? (existing.name || '') : '',
                      path: existing.rid === nextRid ? (existing.path || '') : '',
                      error: ''
                    };
                    ctrl.updateValue(option.key, templateDeviceSelector.composeTargetDeviceIds(devicePicker.requiredSelections));
                  },
                  onBlurRid: function (index, value) {
                    var normalizedRid = templateDeviceSelector.normalizeRid(value);
                    templateDeviceSelector.resolveDeviceByRidWithError(normalizedRid)
                      .then(function (result) {
                        var selections = devicePicker.requiredSelections.slice();
                        var existing = selections[index];
                        if (!existing) return;
                        if (templateDeviceSelector.normalizeRid(existing.rid) !== normalizedRid) return;
                        selections[index] = Object.assign({}, existing, {
                          rid: (result && result.device && result.device.rid) || existing.rid,
                          name: (result && result.device && result.device.name) || '',
                          path: (result && result.device && result.device.path) || '',
                          error: (result && result.error) || ''
                        });
                        devicePicker.requiredSelections = selections;
                        ctrl.updateValue(option.key, templateDeviceSelector.composeTargetDeviceIds(selections));
                        m.redraw();
                      });
                  },
                  onClear: function (index) {
                    var requiredDevice = requiredDevices[index] || {};
                    devicePicker.requiredSelections[index] = {
                      templateKey: requiredDevice.key,
                      rid: '',
                      name: '',
                      path: '',
                      error: ''
                    };
                    ctrl.updateValue(option.key, templateDeviceSelector.composeTargetDeviceIds(devicePicker.requiredSelections));
                  },
                  onDeleteRow: function (index) {
                    devicePicker.requiredSelections.splice(index, 1);
                    if (devicePicker.expandedIndex === index) {
                      devicePicker.expandedIndex = -1;
                      devicePicker.selected(null);
                    } else if (devicePicker.expandedIndex > index) {
                      devicePicker.expandedIndex = devicePicker.expandedIndex - 1;
                    }
                    ctrl.updateValue(option.key, templateDeviceSelector.composeTargetDeviceIds(devicePicker.requiredSelections));
                  },
                  onAddRow: function () {
                    devicePicker.requiredSelections.push({
                      templateKey: 'custom-device-' + Date.now() + '-' + devicePicker.requiredSelections.length,
                      rid: '',
                      name: '',
                      path: ''
                    });
                  }
                })
              : null,
            templateDeviceParseError ? m('div.f6.mid-gray.mb2', templateDeviceParseError) : null,
            !requiredDevices.length
              ? templateDeviceSelector.renderRequiredDeviceSelector({
                  requiredDevices: [],
                  selections: devicePicker.overrideSelections,
                  expandedIndex: devicePicker.overrideExpandedIndex,
                  selectedNode: devicePicker.overrideSelected,
                  allowAddRemoveRows: true,
                  sectionLabel: '',
                  rowCountLabel: '',
                  sectionHint: templateDeviceSelector.buildTargetDeviceHelpText(0),
                  onToggleExpand: function (index) {
                    devicePicker.overrideExpandedIndex = devicePicker.overrideExpandedIndex === index ? -1 : index;
                    devicePicker.overrideSelected(null);
                  },
                  onSelect: function (_index, selectedDevice) {
                    if (!selectedDevice || !selectedDevice['@rid']) return;
                    var rid = String(selectedDevice['@rid']);
                    var selections = devicePicker.overrideSelections.slice();
                    selections[_index] = Object.assign({}, selections[_index] || {}, {
                      templateKey: (selections[_index] && selections[_index].templateKey) || (option.key + '-override-' + _index),
                      rid: rid,
                      name: selectedDevice.name ? String(selectedDevice.name) : '',
                      path: selectedDevice.path ? String(selectedDevice.path) : '',
                      error: ''
                    });
                    devicePicker.overrideSelections = selections;
                    ctrl.updateValue(option.key, templateDeviceSelector.composeTargetDeviceIds(selections));
                    devicePicker.overrideExpandedIndex = -1;
                    devicePicker.overrideSelected(null);
                  },
                  onChangeRid: function (_index, value) {
                    var nextRid = String(value || '').trim();
                    var selections = devicePicker.overrideSelections.slice();
                    var existing = selections[_index] || {};
                    selections[_index] = {
                      templateKey: existing.templateKey || (option.key + '-override-' + _index),
                      rid: nextRid,
                      name: existing.rid === nextRid ? String(existing.name || '') : '',
                      path: existing.rid === nextRid ? String(existing.path || '') : '',
                      error: ''
                    };
                    devicePicker.overrideSelections = selections;
                    ctrl.updateValue(option.key, templateDeviceSelector.composeTargetDeviceIds(selections));
                  },
                  onBlurRid: function (_index, value) {
                    var normalizedRid = templateDeviceSelector.normalizeRid(value);
                    templateDeviceSelector.resolveDeviceByRidWithError(normalizedRid)
                      .then(function (result) {
                        var selections = devicePicker.overrideSelections.slice();
                        var existing = selections[_index];
                        if (!existing) return;
                        if (templateDeviceSelector.normalizeRid(existing.rid) !== normalizedRid) return;
                        selections[_index] = Object.assign({}, existing, {
                          rid: (result && result.device && result.device.rid) || existing.rid,
                          name: (result && result.device && result.device.name) || '',
                          path: (result && result.device && result.device.path) || '',
                          error: (result && result.error) || ''
                        });
                        devicePicker.overrideSelections = selections;
                        ctrl.updateValue(option.key, templateDeviceSelector.composeTargetDeviceIds(selections));
                        m.redraw();
                      });
                  },
                  onClear: function (_index) {
                    var selections = devicePicker.overrideSelections.slice();
                    var existing = selections[_index] || {};
                    selections[_index] = {
                      templateKey: existing.templateKey || (option.key + '-override-' + _index),
                      rid: '',
                      name: '',
                      path: '',
                      error: ''
                    };
                    devicePicker.overrideSelections = selections;
                    ctrl.updateValue(option.key, templateDeviceSelector.composeTargetDeviceIds(selections));
                  },
                  onDeleteRow: function (_index) {
                    var selections = devicePicker.overrideSelections.slice();
                    selections.splice(_index, 1);
                    if (!selections.length) {
                      selections.push({
                        templateKey: option.key + '-override-0',
                        rid: '',
                        name: '',
                        path: ''
                      });
                    }
                    devicePicker.overrideSelections = selections;
                    ctrl.updateValue(option.key, templateDeviceSelector.composeTargetDeviceIds(selections));
                    if (devicePicker.overrideExpandedIndex === _index) {
                      devicePicker.overrideExpandedIndex = -1;
                      devicePicker.overrideSelected(null);
                    } else if (devicePicker.overrideExpandedIndex > _index) {
                      devicePicker.overrideExpandedIndex = devicePicker.overrideExpandedIndex - 1;
                    }
                  },
                  onAddRow: function () {
                    var selections = devicePicker.overrideSelections.slice();
                    selections.push({
                      templateKey: option.key + '-override-' + Date.now() + '-' + selections.length,
                      rid: '',
                      name: '',
                      path: ''
                    });
                    devicePicker.overrideSelections = selections;
                    ctrl.updateValue(option.key, templateDeviceSelector.composeTargetDeviceIds(selections));
                    devicePicker.overrideExpandedIndex = -1;
                    devicePicker.overrideSelected(null);
                  }
                })
              : null
          ]);
        }

        if (option.key === 'templateContent') {
          var filenameKey = option.key + '_filename';
          var modeKey = option.key + '_mode';
          var mode = ctrl.getValue(modeKey, 'paste');
          var filename = ctrl.getValue(filenameKey, '');
          var isApplyTemplateMode = ctrl.isApplyTemplateCommand(selected);
          var isBulkTemplateMode = ctrl.isBulkTemplateCommand(selected);
          var canPickTemplateMode = isApplyTemplateMode || isBulkTemplateMode;
          var targetEquipmentIdForTemplate = ctrl.getValue('targetEquipmentId', '');
          var classTemplates = ctrl.classTemplatePicker.classTemplates;
          var hasClassTemplates = classTemplates.length > 0;
          var setMode = function (nextMode) {
            if (nextMode === 'class-template') {
              if (isBulkTemplateMode) {
                ctrl.activateBulkClassTemplateMode();
                return;
              }
              ctrl.activateClassTemplateMode(targetEquipmentIdForTemplate);
              return;
            }
            ctrl.updateValue(modeKey, nextMode);
            ctrl.classTemplatePicker.classTemplatesError = '';
            ctrl.classTemplatePicker.selectedTemplateError = '';
            if (nextMode === 'upload') {
              ctrl.updateValue(option.key, '');
              ctrl.formValues[filenameKey] = '';
            } else {
              ctrl.formValues[filenameKey] = '';
            }
          };
          return m('div.mb3.ba.b--moon-gray.br2.pa3', [
	            m('div.flex.items-center.justify-between.mb2', [
	              m('div.flex.items-center', [
	                m('span.f3', 'Set template'),
	                optionHelpIcon
	              ]),
              m(templateInputModeTabs, {
                className: 'flex',
                activeKey: mode,
                onSelect: setMode,
                tabs: [
                  canPickTemplateMode ? { key: 'class-template', label: 'Stored Templates' } : null,
                  { key: 'paste', label: 'Paste JSON' },
                  { key: 'upload', label: 'Upload File' }
                ].filter(Boolean)
              })
            ]),
            mode === 'class-template'
              ? m('div', [
                  m('label.db.f5.mb1', { for: 'binding-template-selector' }, 'Template'),
                  m('select#binding-template-selector.w-100.ba.b--mid-gray.br2.pa2.mb2', {
                    value: ctrl.classTemplatePicker.selectedClassTemplateRid,
                    disabled: ctrl.classTemplatePicker.loadingClassTemplates || ctrl.classTemplatePicker.loadingSelectedTemplate || !hasClassTemplates,
                    onchange: function (event) {
                      var selectedTemplateRid = event && event.target ? event.target.value : '';
                      var selectedTemplate = classTemplates.find(function (template) {
                        return template && template.equipmentRid === selectedTemplateRid;
                      }) || null;
                      ctrl.loadClassTemplateIntoTemplateContent(selectedTemplateRid, selectedTemplate);
                    }
                  }, hasClassTemplates
                    ? classTemplates.map(function (template) {
                        return m('option', { value: template.equipmentRid, key: template.equipmentRid }, formatClassTemplateLabel(template));
                      })
                    : [m('option', { value: '', key: 'no-template-options' }, 'No templates available')]
                  ),
                  ctrl.classTemplatePicker.loadingClassTemplates ? m('div.f6.gray.mb2', 'Loading class templates...') : null,
                  ctrl.classTemplatePicker.classTemplatesError ? m('div.f6.gray.mb2', ctrl.classTemplatePicker.classTemplatesError) : null,
                  ctrl.classTemplatePicker.loadingSelectedTemplate ? m('div.f6.gray.mb2', 'Loading template JSON...') : null,
                  ctrl.classTemplatePicker.selectedTemplateError ? m('div.f6.red.mb2', ctrl.classTemplatePicker.selectedTemplateError) : null,
                  m('textarea.w-100.ba.b--mid-gray.br2.pa2', {
                    id: inputId,
                    value: value,
                    readonly: true,
                    style: {
                      height: '45vh',
                      resize: 'vertical'
                    }
                  })
                ])
              : mode === 'upload'
              ? m('div', [
                  m(fileInput, {
                    field: {
                      id: inputId,
                      name: 'Upload File',
                      accept: '.json,.txt'
                    },
                    onChange: function (file) {
                      if (!file) {
                        ctrl.formValues[filenameKey] = '';
                        ctrl.updateValue(option.key, '');
                        return;
                      }
                      var reader = new FileReader();
                      reader.onload = function (ev) {
                        ctrl.formValues[filenameKey] = file.name;
                        ctrl.updateValue(option.key, ev.target.result);
                        m.redraw();
                      };
                      reader.readAsText(file);
                    }
                  }),
                  filename ? m('div.f6.mid-gray.mb2', filename) : m('div.f6.mid-gray.mb2', 'No file selected')
                ])
              : m('textarea.w-100.ba.b--mid-gray.br2.pa2', Object.assign({
                  id: inputId,
                  value: value
                }, attrs))
          ]);
        }

        if (option.type === 'text' || option.type === 'list') {
          return m('div.mb3', [
            m('div.flex.items-center.mb2', [
              m('label.db.f3.mb0', { for: inputId }, fieldLabel),
              optionHelpIcon
            ]),
            m('textarea.h4.w-100.ba.b--mid-gray.br2.pa2', Object.assign({
              value: value
            }, attrs))
          ]);
        }

        return m('div.mb3', [
          m('div.flex.items-center.mb2', [
            m('label.db.f3.mb0', { for: inputId }, fieldLabel),
            optionHelpIcon
          ]),
          m('input.w-100.ba.b--mid-gray.br2.pa2', Object.assign({
            type: 'text',
            value: value
          }, attrs)),
          isBulkSearchPatternField
            ? m('div.f6.gray.mt1', restrictToServerEnabledForSearch
              ? [
                  'This search pattern is used to find a device by the equipment name. Leave it blank to use the equipment name. Or enter a string and use ',
                  m('code', '{Name}'),
                  ' to inject the equipment name. e.g. ',
                  m('code', 'Example Building - Level 4 - {Name}'),
                  ' searches for a device named ',
                  m('code', 'Example Building - Level 4 - AHU-3')
                ]
              : [
                  'This search pattern is used to find a BACnet Host by the equipment name. Leave it blank to use the equipment name. Or enter a string and use ',
                  m('code', '{Name}'),
                  ' to inject the equipment name. e.g. ',
                  m('code', 'Example Building - Level 4 - {Name}'),
                  ' searches for a BACnet Host named ',
                  m('code', 'Example Building - Level 4 - AHU-3')
                ])
            : null
        ]);
      }),
      m('div.mt4.flex.items-center', [
        m(button, {
          label: loaders.submit.show() ? 'Running...' : 'Run Command',
          class: 'btn-good dib ph4 pv2 ' + (loaders.submit.show() ? 'o-50' : ''),
          func: function () {
            if (!loaders.submit.show()) {
              ctrl.submit();
            }
          }
        }),
        ctrl.result() ? m('span.ml3.mid-gray', 'Exit code: ' + (typeof ctrl.result().exitCode === 'number' ? ctrl.result().exitCode : 'n/a')) : null
      ])
    ]) : null,
    ctrl.result() ? m('div.mt4', [
      m('div.f3.pb2', 'Command Output'),
      ctrl.result().download ? m('div.ba.b--mid-gray.pa3.br2.mb3', [
        m('div.flex.items-center.justify-between.mb2', [
          m('div.f3', 'Template download ready'),
          m('span.code.f6.ba.b--mid-gray.br2.ph2.pv1', ctrl.result().download.fileName)
        ]),
        m('div.tr', [
          m(button, {
            label: 'Download again',
            class: 'btn-good dib',
            func: function () {
              ctrl.triggerDownload(ctrl.result().download);
            }
          })
        ])
      ]) : null,
      m('div.flex.flex-column.flex-row-ns', [
        m('div.w-100.w-50-ns.pr0.pr3-ns', [
          m('div.f6.b.mb1', 'STDOUT'),
          m('pre.code.ba.b--mid-gray.pa2', {
            style: { maxHeight: '20rem', overflow: 'auto' }
          }, ctrl.result().stdout ? ctrl.result().stdout : '(no output)')
        ]),
        m('div.w-100.w-50-ns.pl0.pl3-ns.mt3.mt0-ns', [
          m('div.f6.b.mb1', 'STDERR'),
          m('pre.code.ba.b--mid-gray.pa2', {
            style: { maxHeight: '20rem', overflow: 'auto' }
          }, ctrl.result().stderr ? ctrl.result().stderr : '(no output)')
        ])
      ])
    ]) : null
  ]);
};

module.exports = bindingPage;
